import { useLayoutEffect, useRef, useState } from 'react'

export const useScaledMapLayout = ({ containerRef, mapRef, height }: ScaledMapLayoutParams) => {
  const [ layout, setLayout ] = useState<ScaledMapLayout>({ width: 0, scale: 1 })
  const currentLayout = useRef(layout)

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return

    let frame = 0
    const update = () => {
      frame = 0
      const width = Number.parseFloat(getComputedStyle(container).width)
      const physicalWidth = container.getBoundingClientRect().width
      if (!width || !physicalWidth) return

      const scale = physicalWidth / width
      const previous = currentLayout.current
      if (previous.width !== width || previous.scale !== scale) {
        const next = { width, scale }
        currentLayout.current = next
        setLayout(next)
      } else {
        mapRef.current?.resize()
      }
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update)
    }
    const observer = new ResizeObserver(schedule)
    observer.observe(container)
    observer.observe(document.documentElement)
    const scaleObserver = new MutationObserver(schedule)
    let ancestor: HTMLElement | null = container.parentElement
    while (ancestor) {
      scaleObserver.observe(ancestor, { attributes: true, attributeFilter: [ 'style' ] })
      ancestor = ancestor.parentElement
    }
    window.addEventListener('resize', schedule)
    document.addEventListener('transitionend', schedule, true)
    document.addEventListener('animationend', schedule, true)
    update()

    return () => {
      observer.disconnect()
      scaleObserver.disconnect()
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', schedule)
      document.removeEventListener('transitionend', schedule, true)
      document.removeEventListener('animationend', schedule, true)
    }
  }, [ containerRef, mapRef ])

  useLayoutEffect(() => {
    mapRef.current?.resize()
  }, [ layout, mapRef ])

  return {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: layout.width || '100%',
    height,
    zoom: 1 / layout.scale,
    transform: `scale(${layout.scale})`,
    transformOrigin: 'top left',
  }
}
